import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { bugReportsApi } from 'services/apiDigitalTicket/bugReports';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as BugReportsActions from './actions';
import { BugReportsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof BugReportsActions.listBugReportsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando ajustes' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        BugReportsActions.listBugReportsSuccess({
          bugReportsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - listBugReports - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.listBugReportsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof BugReportsActions.getBugReportRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BugReportsActions.getBugReportSuccess({ bugReportData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - getBugReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.getBugReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof BugReportsActions.filterBugReportsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BugReportsActions.filterBugReportsSuccess({
          bugReportsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - filterBugReports - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.filterBugReportsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof BugReportsActions.createBugReportRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando registro de ajustes' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BugReportsActions.createBugReportSuccess({
          bugReportData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'ajustes do sistema criado com sucesso',
        }),
      );
      yield put(push(PATH.BUG_REPORTS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - createBugReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.createBugReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof BugReportsActions.updateBugReportRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Atualizando registro de ajustes',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BugReportsActions.updateBugReportSuccess({
          bugReportData: response.data,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'ajustes do sistema alterado com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(BugReportsActions.listBugReportsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - updateBugReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.updateBugReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof BugReportsActions.deleteBugReportRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo registro' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(bugReportsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ajuste excluído com sucesso',
        }),
      );
      yield put(BugReportsActions.listBugReportsRequest({}));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('bugReports - deleteBugReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BugReportsActions.deleteBugReportFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const bugReportsSagas = [
  takeLatest(BugReportsTypes.LIST_BUG_REPORTS_REQUEST, list),
  takeLatest(BugReportsTypes.GET_BUG_REPORT_REQUEST, get),
  takeLatest(BugReportsTypes.FILTER_BUG_REPORTS_REQUEST, filter),
  takeLatest(BugReportsTypes.CREATE_BUG_REPORT_REQUEST, create),
  takeLatest(BugReportsTypes.UPDATE_BUG_REPORT_REQUEST, update),
  takeLatest(BugReportsTypes.DELETE_BUG_REPORT_REQUEST, remove),
];
